import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { LockPartnerProjectPricingDTO } from "@/modules/packages/domain/contracts";
import {
  useLockPartnerProjectPricing,
  usePartnerProjectSaleQuery,
} from "@/modules/packages/presentation/api/queries";
import { formatDate } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SellPackageDiscountTypeRadio } from "@/shared/ui/SellPackageDiscountTypeRadio";
import {
  buildEqualCreditInstallments,
  formatInstallmentTotalMismatchMessage,
  getInstallmentPlanBalance,
  sumInstallmentAmounts,
  type CreditInstallmentPlanRow,
} from "@/shared/lib/credit-invoice-installments";
import {
  Button,
  FormPrice,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import { LockIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { z } from "zod";

const LockFormSchema = z
  .object({
    price: z.coerce.number().int().min(1, "ລາຄາຕ້ອງຫຼາຍກວ່າ 0"),
    discountType: z.enum(["percentage", "fixed", "none"]).optional(),
    discountValue: z.number().optional(),
    settlementMode: z.enum(["receipt", "credit"]).default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).optional().default(0),
    installmentCount: z.coerce.number().int().min(2).max(12).optional(),
    installmentRows: z
      .array(
        z.object({
          installmentNo: z.number(),
          label: z.string(),
          dueDate: z.string(),
          amount: z.coerce.number().int().min(1),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.settlementMode !== "credit") return;
    let finalPrice = data.price;
    if (
      data.discountType &&
      data.discountType !== "none" &&
      data.discountValue != null
    ) {
      const discountAmount =
        data.discountType === "percentage"
          ? Math.round((data.price * data.discountValue) / 100)
          : Math.round(data.discountValue);
      finalPrice = Math.max(0, data.price - Math.min(discountAmount, data.price));
    }
    const rows = data.installmentRows ?? [];
    if (rows.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງກຳນົດແຜນງວດຊຳລະ",
        path: ["installmentRows"],
      });
    } else if (sumInstallmentAmounts(rows) !== finalPrice) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatInstallmentTotalMismatchMessage(
          sumInstallmentAmounts(rows),
          finalPrice,
        ),
        path: ["installmentRows"],
      });
    }
  });

type LockFormValues = z.infer<typeof LockFormSchema>;

export function PartnerProjectLockPricingPage() {
  const { id } = useParams({ from: "/app/sell/partner/$id/lock-pricing" });
  const nav = useNavigate();
  const saleQuery = usePartnerProjectSaleQuery(id);
  const lockMutation = useLockPartnerProjectPricing(id);
  const sale = saleQuery.data;

  const methods = RHF.useForm<LockFormValues>({
    resolver: zodResolver(LockFormSchema),
    defaultValues: {
      price: 0,
      discountType: "none",
      settlementMode: "receipt",
      amountPaidNow: 0,
      installmentCount: 2,
      installmentRows: [],
    },
  });

  useEffect(() => {
    if (!sale) return;
    methods.reset({
      price: sale.price ?? sale.provisionalTotal ?? 0,
      discountType: sale.discountType ?? "none",
      discountValue: sale.discountValue ?? undefined,
      settlementMode: "receipt",
      amountPaidNow: 0,
      installmentCount: 2,
      installmentRows: [],
    });
  }, [sale, methods]);

  const watchedPrice = methods.watch("price");
  const discountType = methods.watch("discountType");
  const discountValue = methods.watch("discountValue");
  const settlementMode = methods.watch("settlementMode");
  const installmentCount = methods.watch("installmentCount");
  const installmentRows = methods.watch("installmentRows") ?? [];

  const finalPrice = useMemo(() => {
    if (!watchedPrice || watchedPrice <= 0) return 0;
    if (!discountType || discountType === "none" || discountValue == null) {
      return watchedPrice;
    }
    const discountAmount =
      discountType === "percentage"
        ? Math.round((watchedPrice * discountValue) / 100)
        : Math.round(discountValue);
    return Math.max(0, watchedPrice - Math.min(discountAmount, watchedPrice));
  }, [watchedPrice, discountType, discountValue]);

  useEffect(() => {
    if (settlementMode !== "credit" || finalPrice <= 0) return;
    const count = installmentCount ?? 2;
    const start = sale?.startDate ?? format(new Date(), "yyyy-MM-dd");
    const rows = buildEqualCreditInstallments(finalPrice, count, start, 30);
    methods.setValue("installmentRows", rows);
  }, [settlementMode, finalPrice, installmentCount, sale?.startDate, methods]);

  const installmentBalance = useMemo(
    () => getInstallmentPlanBalance(installmentRows, finalPrice),
    [installmentRows, finalPrice],
  );

  const onSubmit = async (data: LockFormValues) => {
    const dt =
      data.discountType && data.discountType !== "none"
        ? data.discountType
        : undefined;
    const payload: LockPartnerProjectPricingDTO = {
      price: data.price,
      discountType: dt,
      discountValue:
        dt && data.discountValue != null ? data.discountValue : undefined,
      settlementMode: data.settlementMode,
      amountPaidNow:
        data.settlementMode === "credit"
          ? Math.min(data.amountPaidNow ?? 0, finalPrice)
          : 0,
      installments:
        data.settlementMode === "credit" && data.installmentRows?.length
          ? data.installmentRows
          : undefined,
    };
    const result = await lockMutation.mutateAsync(payload);
    nav({
      to: "/app/sell/success",
      search: {
        type: "partner-off-site",
        partnerSaleId: result.id,
        partnerLabel: result.partnerLabel ?? undefined,
        partnerOffSiteDistance: result.offSiteDistance,
        partnerClassLabels: result.classLabels.join("|"),
        partnerInstructorLabels: result.instructorLabels.join("|"),
        partnerSoldAt: result.createdAt,
        startDate: result.startDate,
        expireDate: result.expireDate,
        originalPrice: result.originalPrice?.toString() ?? "",
        finalPrice: result.finalPrice?.toString() ?? "",
        discountAmount: result.discountAmount?.toString() ?? "",
        discountType: result.discountType ?? "",
        discountValue: result.discountValue?.toString() ?? "",
        receiptId: result.receiptId ?? "",
        settlementMode: result.settlementMode,
        creditInvoiceId: result.credit?.invoiceId,
        creditPaid: result.credit ? String(result.credit.paidAmount) : undefined,
        creditRemaining: result.credit
          ? String(result.credit.remainingAmount)
          : undefined,
        partnerPricingStatus: "locked",
        partnerInstallmentCount:
          result.installmentCount != null
            ? String(result.installmentCount)
            : undefined,
      },
    });
  };

  if (saleQuery.isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb items={[{ label: "Lock ລາຄາ Partner" }]} />
        </Header>
        <Main>
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  if (!sale) {
    return (
      <>
        <Header>
          <Breadcrumb items={[{ label: "Lock ລາຄາ Partner" }]} />
        </Header>
        <Main>
          <p className="text-destructive text-sm">ບໍ່ພົບໂຄງການ</p>
        </Main>
      </>
    );
  }

  if (sale.pricingStatus === "locked") {
    return (
      <>
        <Header>
          <Breadcrumb items={[{ label: "Lock ລາຄາ Partner" }]} />
        </Header>
        <Main>
          <p className="text-muted-foreground text-sm">
            ໂຄງການນີ້ lock ລາຄາແລ້ວ
          </p>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ຂາຍ", to: "/app/sell" },
            { label: "Lock ລາຄາ Partner" },
          ]}
        />
      </Header>
      <Main>
        <div className="mx-auto max-w-2xl space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <LockIcon className="size-5 text-primary" />
              <h1 className="font-semibold text-lg">Lock ລາຄາ & ເກັບເງິນ</h1>
            </div>
            <p className="text-muted-foreground text-sm">
              {sale.partnerLabel ?? "Partner off-site"} ·{" "}
              {formatDate(sale.startDate)} – {formatDate(sale.expireDate)} ·
              ສະຖານະ:{" "}
              <span className="font-medium text-foreground">
                {sale.pricingStatus}
              </span>
            </p>
            {sale.provisionalTotal != null && sale.provisionalTotal > 0 ? (
              <p className="mt-2 text-sm">
                ຍອດປະມານ:{" "}
                <span className="font-medium tabular-nums">
                  {sale.provisionalTotal.toLocaleString("lo-LA")} ກີບ
                </span>
              </p>
            ) : null}
          </div>

          <FormRoot
            methods={methods}
            onSubmit={onSubmit}
            className="space-y-5 rounded-xl border bg-card p-6"
          >
            <FormPrice
              name="price"
              label="ຍອດຕາມສັນຍາ (ກີບ)"
              requiredMark
              currencySymbol="₭"
            />

            <RHF.Controller
              name="discountType"
              control={methods.control}
              render={({ field }) => (
                <SellPackageDiscountTypeRadio
                  value={field.value ?? "none"}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            {(discountType === "percentage" || discountType === "fixed") && (
              <FormPrice
                name="discountValue"
                label={
                  discountType === "percentage" ? "ສ່ວນຫຼຸດ (%)" : "ສ່ວນຫຼຸດ (ກີບ)"
                }
                currencySymbol={discountType === "fixed" ? "₭" : undefined}
              />
            )}

            {finalPrice > 0 ? (
              <p className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                ລາຄາສຸດທ້າຍ:{" "}
                <span className="font-semibold tabular-nums">
                  {finalPrice.toLocaleString("lo-LA")} ກີບ
                </span>
              </p>
            ) : null}

            <RHF.Controller
              name="settlementMode"
              control={methods.control}
              render={({ field }) => (
                <fieldset className="space-y-2 border-0 p-0">
                  <legend className="font-medium text-sm">ຮູບແບບການເງິນ</legend>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(["receipt", "credit"] as const).map((mode) => (
                      <label
                        key={mode}
                        className="flex cursor-pointer items-center gap-2 rounded-lg border p-3"
                      >
                        <input
                          type="radio"
                          checked={field.value === mode}
                          onChange={() => field.onChange(mode)}
                        />
                        <span className="text-sm">
                          {mode === "receipt"
                            ? "ໃບເກັບເງິນເຕັມ"
                            : "ຕິດໜີ້ / แบ่งจ่าย"}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            />

            {settlementMode === "credit" && finalPrice > 0 ? (
              <div className="space-y-3">
                <RHF.Controller
                  name="installmentCount"
                  control={methods.control}
                  render={({ field }) => (
                    <label className="block space-y-1 text-sm">
                      <span className="font-medium">ຈຳນວນງວດ</span>
                      <input
                        type="number"
                        min={2}
                        max={12}
                        className="w-full rounded-md border px-3 py-2"
                        value={field.value ?? 2}
                        onChange={(e) =>
                          field.onChange(Number.parseInt(e.target.value, 10))
                        }
                      />
                    </label>
                  )}
                />
                {installmentRows.map((row, index) => (
                  <FormPrice
                    key={row.installmentNo}
                    name={`installmentRows.${index}.amount`}
                    label={`${row.label} · ${formatDate(row.dueDate)}`}
                    currencySymbol="₭"
                  />
                ))}
                {!installmentBalance.isValid ? (
                  <p className="text-destructive text-xs">
                    {formatInstallmentTotalMismatchMessage(
                      installmentBalance.sum,
                      installmentBalance.total,
                    )}
                  </p>
                ) : null}
                <FormPrice
                  name="amountPaidNow"
                  label="ຮັບຕອນ lock (0 ได้)"
                  currencySymbol="₭"
                  min={0}
                />
              </div>
            ) : null}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/sell" })}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" disabled={lockMutation.isPending}>
                {lockMutation.isPending ? "ກຳລັງ lock..." : "Lock & ເກັບເງິນ"}
              </Button>
            </div>
          </FormRoot>
        </div>
      </Main>
    </>
  );
}
